import { Badge, Card, CardContent } from '@sea-snag/ui';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { type KeyboardEvent } from 'react';

import { type NotificationItem } from '../../lib/api-client';
import { formatPct, formatPrice } from '../../lib/format';
import {
  extractNotificationPayload,
  formatNotificationWeek,
  NOTIFICATION_COLORS,
  NOTIFICATION_LABELS,
  type NotificationPayload,
} from '../../lib/notifications';
import { formatRelativeTime } from '../../lib/time';
import { NotificationIcon } from '../notification-icon';

function formatChangeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ActivityRowProps {
  notification: NotificationItem;
  onClick: (notification: NotificationItem) => void;
}

export function ActivityRow({ notification, onClick }: ActivityRowProps) {
  const payload = extractNotificationPayload(notification);
  const isUnread = notification.readAt === null;

  const handleClick = () => onClick(notification);
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(notification);
    }
  };

  if (typeof payload.changePct === 'number') {
    return (
      <PriceChangeRow
        notification={notification}
        payload={payload}
        isUnread={isUnread}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <GenericRow
      notification={notification}
      payload={payload}
      isUnread={isUnread}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    />
  );
}

interface RowProps {
  notification: NotificationItem;
  payload: NotificationPayload;
  isUnread: boolean;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
}

const CARD_BASE_CLASS =
  'cursor-pointer transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

function PriceChangeRow({ notification, payload, isUnread, onClick, onKeyDown }: RowProps) {
  const change = payload.changePct ?? 0;
  const isDrop = change < 0;
  const isFlat = change === 0;
  const directionClass = isFlat
    ? 'text-amber-600 dark:text-amber-400'
    : isDrop
      ? 'text-red-600 dark:text-red-400'
      : 'text-green-600 dark:text-green-400';
  const Icon = isFlat ? null : isDrop ? TrendingDown : TrendingUp;

  // Prefer the snapshot's actual observation time; fall back to when we
  // recorded the notification for older rows that don't carry fetchedAt.
  const changeAt = payload.fetchedAt ?? notification.createdAt;
  const changeAtAbsolute = formatChangeTime(changeAt);
  const weekRange = formatNotificationWeek(payload.dateFrom, payload.dateTo);
  const intel = payload.intelligence;

  return (
    <Card
      role="button"
      tabIndex={0}
      className={CARD_BASE_CLASS}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <CardContent className="flex items-start gap-3 px-3 py-2">
        <div
          className={`mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-muted ${directionClass}`}
        >
          {Icon ? <Icon className="size-4" /> : <span className="text-base">↔</span>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={`truncate text-sm ${isUnread ? 'font-medium' : 'text-muted-foreground'}`}>
              {payload.marketBoatName ?? '-'}
              {payload.charterName ? (
                <span className="ml-2 font-normal text-muted-foreground">
                  · {payload.charterName}
                </span>
              ) : null}
            </p>
            {isUnread && <span className="size-1.5 shrink-0 rounded-full bg-blue-500" />}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
            <Badge
              variant="secondary"
              className={`text-[10px] ${NOTIFICATION_COLORS[payload.ruleType] ?? ''}`}
            >
              {NOTIFICATION_LABELS[payload.ruleType] ?? 'Alert'}
            </Badge>
            {payload.compSetName ? (
              <span className="truncate text-muted-foreground">in {payload.compSetName}</span>
            ) : null}
            {weekRange ? <span className="text-muted-foreground">Week {weekRange}</span> : null}
            <span className="text-muted-foreground">{changeAtAbsolute}</span>
          </div>
          {intel ? <IntelligenceLine intel={intel} currency={payload.currency} /> : null}
        </div>
        <div className="mt-1 flex shrink-0 items-center gap-3 text-sm">
          <span className={`font-mono tabular-nums ${directionClass}`}>{formatPct(change)}</span>
          <span className="font-mono tabular-nums">
            <span className="text-muted-foreground line-through">
              {formatPrice(payload.oldPrice, payload.currency)}
            </span>
            <span className="mx-1 text-muted-foreground">→</span>
            <span>{formatPrice(payload.newPrice, payload.currency)}</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function IntelligenceLine({
  intel,
  currency,
}: {
  intel: NonNullable<NotificationPayload['intelligence']>;
  currency: string | undefined;
}) {
  const { rank, userBoats, userMedianPrice } = intel;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
      <span className="font-mono tabular-nums text-muted-foreground">
        Rank #{rank.previous} → #{rank.current} of {rank.total}
      </span>
      {userBoats.length > 0 ? (
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-muted-foreground">Your boats:</span>
          {userBoats.map((b) => {
            const above = b.vsCompetitorPct > 0;
            const flat = b.vsCompetitorPct === 0;
            const arrow = flat ? '▬' : above ? '▲' : '▼';
            const colorClass = flat
              ? 'text-muted-foreground'
              : above
                ? 'text-red-600 dark:text-red-400'
                : 'text-green-600 dark:text-green-400';
            return (
              <span key={b.pid} className="inline-flex items-center gap-1">
                <span>{b.name}</span>
                <span className={`font-mono tabular-nums ${colorClass}`}>
                  {arrow}
                  {Math.abs(b.vsCompetitorPct)}%
                </span>
              </span>
            );
          })}
          {userMedianPrice ? (
            <span className="text-muted-foreground">
              (median {formatPrice(userMedianPrice, currency)})
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

function GenericRow({ notification, payload, isUnread, onClick, onKeyDown }: RowProps) {
  return (
    <Card
      role="button"
      tabIndex={0}
      className={CARD_BASE_CLASS}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      <CardContent className="flex items-center gap-3 px-3 py-2">
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted">
          <NotificationIcon ruleType={payload.ruleType} className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={`text-sm ${isUnread ? 'font-medium' : 'text-muted-foreground'}`}>
              {payload.message}
            </p>
            {isUnread && <span className="size-1.5 shrink-0 rounded-full bg-blue-500" />}
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <Badge
              variant="secondary"
              className={`text-[10px] ${NOTIFICATION_COLORS[payload.ruleType] ?? ''}`}
            >
              {NOTIFICATION_LABELS[payload.ruleType] ?? notification.type}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {formatRelativeTime(notification.createdAt)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
